import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, AttributionControl } from "react-leaflet";
import SoundMarker from "./SoundMarker";
import { useSelector } from "react-redux";
import useWindowDimensions from "./useWindowDimensions";
import IsLoading from "./IsLoading";
import HasError from "./HasError";

const SoundMap = ({ dataType, setDataType }) => {
  const { screenWidth } = useWindowDimensions();
  const sound = useSelector((state) => state.sound);
  const SPLData = sound.soundData.OSPL;
  const soundLoading = sound.soundLoading;
  const soundError = sound.soundError;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData || [];
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;

  const leqHourly = useSelector((state) => state.leq.leqHourly);
  const leqNighttime = useSelector((state) => state.leq.leqNighttime);
  const leqDaytime = useSelector((state) => state.leq.leqDaytime);
  const leqDaily = useSelector((state) => state.leq.leqDaily);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageData, setPageData] = useState([]);

  const [mapZoom, setMapZoom] = useState(5.6);
  const [mapHeight, setMapHeight] = useState("90vh");

  // Memoize map settings calculation
  useEffect(() => {
    setMapZoom(screenWidth < 600 ? 2 : 5.6);
    setMapHeight(screenWidth < 600 ? "70vh" : "90vh");
  }, [screenWidth]);

  const modifyData = useCallback(
    (data) => {
      // Ensure data is an array before mapping
      return Array.isArray(data)
        ? data.map((station) => {
            const specificStationInformation = informationData.find(
              (specificStation) =>
                specificStation.device_id === station.device_id &&
                specificStation.device === "OSPL" &&
                station.is_test !== true
            );
            return { ...station, ...specificStationInformation };
          })
        : []; // Return an empty array if data is not an array
    },
    [informationData]
  );

  // Memoized data with fallback to empty array if data is not valid
  const modifiedSPLData = useMemo(
    () => modifyData(SPLData || []),
    [SPLData, modifyData]
  );

  const modifiedLeqHourlyData = useMemo(
    () => modifyData(leqHourly.data?.data || []),
    [leqHourly.data, modifyData]
  );

  const modifiedLeqDaytimeData = useMemo(
    () => modifyData(leqDaytime.data?.data || []),
    [leqDaytime.data, modifyData]
  );

  const modifiedLeqNighttimeData = useMemo(
    () => modifyData(leqNighttime.data?.data || []),
    [leqNighttime.data, modifyData]
  );

  const modifiedLeqDAILYData = useMemo(
    () => modifyData(leqDaily.data?.data || []),
    [leqDaily.data, modifyData]
  );

  useEffect(() => {
    if (dataType === "spl") {
      setPageData(modifiedSPLData);
      setLoading(soundLoading);
      setError(soundError);
    } else if (dataType === "hourly") {
      setPageData(modifiedLeqHourlyData);
      setLoading(leqHourly.loading);
      setError(leqHourly.error);
    } else if (dataType === "nighttime") {
      setPageData(modifiedLeqNighttimeData);
      setLoading(leqNighttime.loading);
      setError(leqNighttime.error);
    } else if (dataType === "daytime") {
      setPageData(modifiedLeqDaytimeData);
      setLoading(leqDaytime.loading);
      setError(leqDaytime.error);
    } else if (dataType === "daily") {
      setPageData(modifiedLeqDAILYData);
      setLoading(leqDaily.loading);
      setError(leqDaily.error);
    }
  }, [
    dataType,
    modifiedSPLData,
    modifiedLeqHourlyData,
    modifiedLeqNighttimeData,
    modifiedLeqDaytimeData,
    modifiedLeqDAILYData,
    soundLoading,
    soundError,
    leqHourly.loading,
    leqHourly.error,
    leqNighttime.loading,
    leqNighttime.error,
    leqDaytime.loading,
    leqDaytime.error,
    leqDaily.loading,
    leqDaily.error,
    informationData,
  ]);

  if (soundLoading || informationLoading) {
    return <IsLoading />;
  } else if (soundError || informationError) {
    return <HasError />;
  } else {
    return (
      <div dir="ltr" className="row my-5 mx-md-5">
        <div
          className="bg-secondary rounded-1 p-2"
          dir="rtl"
          style={{ width: "90%", margin: "auto" }}
        >
          <div className="col-md-4">
            <select
              name="average"
              id="average"
              className="form-select w-100 form-select-lg"
              aria-label="Default select example"
              onChange={(e) => setDataType(e.target.value)}
              value={dataType}
            >
              <option value="spl">SPL</option>
              <option value="hourly">LEQ ساعتی</option>
              <option value="nighttime">LEQ شبانه</option>
              <option value="daytime">LEQ روزانه</option>
              <option value="daily">LEQ شبانه روز</option>
            </select>
          </div>
        </div>
        <MapContainer
          id="SoundMap"
          center={[32.739, 53.688]}
          zoom={mapZoom}
          zoomSnap={0.1}
          style={{
            width: "90%",
            height: mapHeight,
            margin: "auto",
          }}
          attributionControl={false}
        >
          {pageData.map((station) => (
            <SoundMarker
              data={station}
              type={station.device === "OSPL" ? "OSPL" : "SATA"}
              key={
                station.slug ||
                station.device + station.idStation ||
                station.device + station.device_id
              }
              dataType={dataType}
            />
          ))}
          <TileLayer
            attribution="ایستگاه های فعال پایش آلودگی صوت"
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AttributionControl position="bottomright" prefix={false} />
        </MapContainer>
      </div>
    );
  }
};

export default SoundMap;
